import React, { useContext, useState } from 'react'

// @mui core components
import { Box, Grid, Typography, Button } from '@mui/material'
import Card from 'atoms/Card'

// Data
import { IntakeContext } from 'context/IntakeContext'
import { selectData } from 'layouts/onboarding'

// Popup
import { toast } from 'react-toastify'
import { renderMuiFormField } from 'utils/forms/renderMuiFormField'

// Debounce
import { useFormDebounce } from 'hooks/useFormDebounce'
import LoadingButton from 'components/LoadingButton'

function MilitaryDetails() {
  const { intakeState, updateIntakeStateAtKey, updateUser } =
    useContext(IntakeContext)

  const { updateIntakeStateAndSubmit, loading } = useFormDebounce()

  // add an onSubmit button and call the context function `updateUser`
  // const onSubmit = () => updateUser()
  const onSubmit = () => {
    updateUser()
    toast.info('You have successfully updated your military details', {
      position: 'top-center',
      autoClose: 5000,
      pauseOnFocusLoss: false,
      pauseOnHover: false,
    })
  }

  const [localForm, setLocalForm] = useState({
    militaryActiveDutyStartDate: intakeState.militaryActiveDutyStartDate || '',
    militaryActiveDutyEndDate: intakeState.militaryActiveDutyEndDate || '',
    militaryReservesStartDate: intakeState.militaryReservesStartDate || '',
    militaryReservesEndDate: intakeState.militaryReservesEndDate || '',
  })
  const handleChange = (field) => (value) => {
    setLocalForm((prev) => ({ ...prev, [field]: value }))
  }

  const showMilitary = intakeState?.military
  const showMilitaryReserves = intakeState?.militaryReserves

  return (
    <>
      <Box
        pl={3}
        display="flex"
        justifyContent="space-between"
        alignItems="center"
      >
        <Typography display="flex" align="left" variant="body2FunHeader">
          MILITARY
        </Typography>

        <LoadingButton loading={loading} onClick={onSubmit} />
      </Box>
      <Card>
        <Box component="form" p={3}>
          <Grid container columnSpacing={3} rowSpacing={2}>
            <Grid item xs={12} sm={6}>
              {renderMuiFormField({
                autoFocus: false,
                id: 'military',
                label: 'Did you serve in the military?',
                placeholder: '',
                inputType: 'select',
                value: intakeState.military,
                onChange: updateIntakeStateAndSubmit((military) =>
                  updateIntakeStateAtKey({ military })
                ),
                options: selectData.yesNo,
              })}
            </Grid>
            {showMilitary ? (
              <>
                <Grid item xs={12} sm={6}>
                  {renderMuiFormField({
                    autoFocus: false,
                    id: 'militaryBranch',
                    label: 'Military Branch',
                    placeholder: '',
                    inputType: 'select',
                    value: intakeState.militaryBranch,
                    onChange: updateIntakeStateAndSubmit((militaryBranch) =>
                      updateIntakeStateAtKey({ militaryBranch })
                    ),
                    options: selectData.militaryBranch,
                  })}
                </Grid>
                <Grid item xs={12} sm={6}>
                  {renderMuiFormField({
                    autoFocus: false,
                    id: 'militaryActiveDutyPension',
                    label: 'Are you collecting an active duty pension?',
                    placeholder: '',
                    inputType: 'select',
                    value: intakeState.militaryActiveDutyPension,
                    onChange: updateIntakeStateAndSubmit(
                      (militaryActiveDutyPension) =>
                        updateIntakeStateAtKey({ militaryActiveDutyPension })
                    ),
                    options: selectData.yesNo,
                  })}
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Grid container columnSpacing={3} rowSpacing={2} pt={1}>
                    <Grid item xs={12} sm={6}>
                      {renderMuiFormField({
                        autoFocus: false,
                        id: 'militaryActiveDutyStartDate',
                        label: 'Active Duty Start Date',
                        inputType: 'date',
                        value: localForm.militaryActiveDutyStartDate,
                        onChange: (e) =>
                          handleChange('militaryActiveDutyStartDate')(e),
                        onBlur: updateIntakeStateAndSubmit(
                          (militaryActiveDutyStartDate) =>
                            updateIntakeStateAtKey({
                              militaryActiveDutyStartDate,
                            })
                        ),
                      })}
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      {renderMuiFormField({
                        autoFocus: false,
                        id: 'militaryActiveDutyEndDate',
                        label: 'Active Duty Discharge Date',
                        inputType: 'date',
                        value: localForm.militaryActiveDutyEndDate,
                        onChange: (e) =>
                          handleChange('militaryActiveDutyEndDate')(e),
                        onBlur: updateIntakeStateAndSubmit(
                          (militaryActiveDutyEndDate) =>
                            updateIntakeStateAtKey({
                              militaryActiveDutyEndDate,
                            })
                        ),
                      })}
                    </Grid>
                  </Grid>
                </Grid>
                <Grid item xs={12} sm={6}>
                  {renderMuiFormField({
                    autoFocus: false,
                    id: 'militaryReserves',
                    label: 'Were you in the reserves?',
                    placeholder: '',
                    inputType: 'select',
                    value: intakeState.militaryReserves,
                    onChange: updateIntakeStateAndSubmit((militaryReserves) =>
                      updateIntakeStateAtKey({ militaryReserves })
                    ),
                    options: selectData.yesNo,
                  })}
                </Grid>
                {showMilitaryReserves && (
                  <>
                    <Grid item xs={12} sm={6}>
                      <Grid container columnSpacing={3} rowSpacing={2} pt={1}>
                        <Grid item xs={12} sm={6}>
                          {renderMuiFormField({
                            autoFocus: false,
                            id: 'militaryReservesStartDate',
                            label: 'Reserves Start Date',
                            inputType: 'date',
                            value: localForm.militaryReservesStartDate,
                            onChange: (e) =>
                              handleChange('militaryReservesStartDate')(e),
                            onBlur: updateIntakeStateAndSubmit(
                              (militaryReservesStartDate) =>
                                updateIntakeStateAtKey({
                                  militaryReservesStartDate,
                                })
                            ),
                          })}
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          {renderMuiFormField({
                            autoFocus: false,
                            id: 'militaryReservesEndDate',
                            label: 'Reserves Discharge Date',
                            inputType: 'date',
                            value: localForm.militaryReservesEndDate,
                            onChange: (e) =>
                              handleChange('militaryReservesEndDate')(e),
                            onBlur: updateIntakeStateAndSubmit(
                              (militaryReservesEndDate) =>
                                updateIntakeStateAtKey({
                                  militaryReservesEndDate,
                                })
                            ),
                          })}
                        </Grid>
                      </Grid>
                    </Grid>
                  </>
                )}
                <Grid item xs={12} sm={6}>
                  {renderMuiFormField({
                    autoFocus: false,
                    id: 'militaryPaidDeposit',
                    label:
                      'Have you paid a deposit to your agency for your active duty time?',
                    placeholder: '',
                    inputType: 'select',
                    value: intakeState.militaryPaidDeposit,
                    onChange: updateIntakeStateAndSubmit(
                      (militaryPaidDeposit) =>
                        updateIntakeStateAtKey({
                          militaryPaidDeposit,
                        })
                    ),
                    options: selectData.yesNo,
                  })}
                </Grid>
              </>
            ) : (
              <></>
            )}
          </Grid>
        </Box>
      </Card>
    </>
  )
}

export default React.memo(MilitaryDetails)
